<script setup lang="ts">
import { defineProps, onErrorCaptured, ref } from 'vue';
import { $t } from '@/composables/translations/useTranslate';
import ProductListPage from './components/pages/ProductListPage.vue';
import OfferPage from './components/pages/OfferPage.vue';
import CheckoutPage from './components/pages/CheckoutPage.vue';
import ThankYouPage from './components/pages/ThankYouPage.vue';
import SearchWidgetPage from './components/pages/SearchWidgetPage.vue';
import ConnectedSearchWidgetPage from './components/pages/ConnectedSearchWidgetPage.vue';
import BaseErrorState from './components/ui/BaseErrorState.vue';
import { useProductListFiltersStore } from '@/stores/productListFilters';

const props = withDefaults(
  defineProps<{
    variant: string;
    targetUrl?: string;
    showSearchBar?: boolean;
    showFilters?: boolean;
  }>(),
  {
    targetUrl: undefined,
    showSearchBar: true,
    showFilters: true,
  }
);
const variant = props.variant;

// Error boundary state
const hasError = ref(false);
const errorMessage = ref('');

// Error boundary handler
onErrorCaptured((err) => {
  hasError.value = true;
  errorMessage.value = err.message || $t('ibe.error.unknown', 'An unexpected error occurred.');
  console.error('App error:', err);
  return false;
});

function getComponentForVariant(variant: string) {
  switch (variant) {
    case 'product-offer':
      return OfferPage;
    case 'checkout':
      return CheckoutPage;
    case 'thank-you':
      return ThankYouPage;
    default:
      return CheckoutPage;
  }
}

function handleReload() {
  useProductListFiltersStore().resetPersistedPeriod();
  window.location.reload();
}
</script>

<template>
  <div v-if="hasError" class="wrapper">
    <BaseErrorState
      icon="icon-warning"
      icon-class="bg-error"
      :title="$t('ibe.error.app-error-title', 'Something went wrong')"
      :message="errorMessage"
      :primary-button="{
        text: $t('ibe.error.app-error-reload', 'Reload page'),
        onClick: handleReload,
      }"
    />
  </div>
  <template v-else>
    <SearchWidgetPage v-if="variant === 'search-widget'" :target-url="props.targetUrl" />
    <ConnectedSearchWidgetPage v-else-if="variant === 'search-widget-connected'" />
    <ProductListPage
      v-else-if="variant === 'product-list'"
      :show-search-bar="props.showSearchBar"
      :show-filters="props.showFilters"
    />
    <component :is="getComponentForVariant(variant)" v-else />
  </template>
</template>
